import type { CartonItem } from "./prepackApi";
import { formatPackQty } from "./formatPackQty";

/** Serialized kit rows (**for example three-phase**) use **`qty × kitDivisor`** physical **`serial_fci`** rows. */
export function serialMultiplierForCartonLine(item: CartonItem): number {
  return item.kitDivisor !== undefined && item.kitDivisor > 1 ? item.kitDivisor : 1;
}

export function physicalDevicesFromLineQty(item: CartonItem, qty: number): number {
  return qty * serialMultiplierForCartonLine(item);
}

export function plannedPhysicalSerials(item: CartonItem): number {
  if (item.plannedPhysicalSerialQty !== undefined) return item.plannedPhysicalSerialQty;
  return Math.round(physicalDevicesFromLineQty(item, item.plannedQty));
}

export function packedPhysicalSerials(item: CartonItem): number {
  if (item.packedPhysicalSerialQty !== undefined) return item.packedPhysicalSerialQty;
  return Math.round(physicalDevicesFromLineQty(item, item.packedQty));
}

export function pendingPhysicalSerials(item: CartonItem): number {
  if (item.pendingPhysicalSerialQty !== undefined) return item.pendingPhysicalSerialQty;
  return Math.round(physicalDevicesFromLineQty(item, item.pending));
}

/**
 * Whole kits plus optional remainder serials. Does **not** mean splitting one kit across cartons —
 * a remainder is **one incomplete kit bundle** placed as a single planner unit.
 */
/** Kit SKU queue/carton lines: packed and planned **`CartonItem`** quantities are kit-equivalents. */
export function formatKitLineProgress(item: CartonItem): string {
  return `${formatPackQty(item.packedQty)} / ${formatPackQty(item.plannedQty)} kits`;
}

/** Non-kit queue/carton lines: physical serialized-device counts (matches carton header totals). */
export function formatLineDeviceProgress(item: CartonItem): string {
  const packed = packedPhysicalSerials(item);
  const planned = plannedPhysicalSerials(item);
  const label = planned === 1 ? "device" : "devices";
  return `${formatPackQty(packed)} / ${formatPackQty(planned)} ${label}`;
}

export function formatKitSerialBreakdown(physicalSerialCount: number, kitDivisor: number): string {
  if (!Number.isFinite(physicalSerialCount) || physicalSerialCount <= 0) {
    return "0 kits";
  }
  const serialTotal = Math.max(0, Math.round(physicalSerialCount));
  const divisor = Math.max(1, Math.round(kitDivisor));
  const completeKits = Math.floor(serialTotal / divisor);
  const remainderSerials = serialTotal % divisor;
  const kitsWord = completeKits === 1 ? "kit" : "kits";
  if (remainderSerials === 0) {
    return `${completeKits} ${kitsWord}`;
  }
  const devicesWord = remainderSerials === 1 ? "serialized device" : "serialized devices";
  return `${completeKits} ${kitsWord} + ${remainderSerials} ${devicesWord} (partial kit — one carton only)`;
}
